import { Link } from "react-router-dom";
import { getBlogPosts } from "@/hooks/useBlogStore";
import { useEffect } from "react";

export default function Blog() {
  const blogPosts = getBlogPosts();
  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  useEffect(() => {
    document.title = "Dental Tourism Blog – Safe Dental Care Abroad Guides | Dental Safety Abroad";

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Practical guides, destination comparisons, and safety advice for dental tourism in Hungary, Poland, Turkey and Spain. Tips on choosing clinics, avoiding red flags, and saving on implants and veneers.');
    setMeta('keywords', 'dental tourism blog,dental implants abroad guide,safe dentistry overseas,dental travel tips,veneers abroad advice');
    setMeta('og:title', 'Dental Tourism Blog – Safe Dental Care Abroad Guides | Dental Safety Abroad', true);
    setMeta('og:description', 'Practical guides, destination comparisons, and safety advice for dental tourism in Europe.', true);

    // Blog listing structured data (ItemList of all articles)
    const listSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": blogPosts.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://dentalsafetyabroad.com/blog/${post.slug}`
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(listSchema);
    script.id = 'blog-list-schema';
    document.head.appendChild(script);

    return () => {
      document.title = 'Dental Safety Abroad – Affordable Dental Care You Can Trust';
      setMeta('description', 'Your independent guide to safe, affordable dental treatment in Hungary, Poland, Turkey and Spain. Take our free quiz to find your best-fit destination.');
      setMeta('keywords', 'dental tourism,dental implants abroad,veneers abroad,Hungary dentist,Poland dentist,Turkey dentist,Spain dentist,affordable dental care');
      const el = document.getElementById('blog-list-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <main className="bg-background-50 min-h-screen pt-24 md:pt-28">
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          <nav className="text-sm text-foreground-500 mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary-700 transition-colors">Home</Link>
              </li>
              <li aria-hidden="true">
                <span className="text-foreground-400">›</span>
              </li>
              <li>
                <span className="text-foreground-700 font-medium">Blog</span>
              </li>
            </ol>
          </nav>

          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-3 block">
            Blog
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
            Insights on Safe Dental Travel
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-10 max-w-2xl">
            Practical guides, destination comparisons, and safety advice to help you make confident decisions about dental treatment abroad.
          </p>

          {/* Featured Post */}
          <Link
            to={`/blog/${featured.slug}`}
            className="block group mb-10"
          >
            <article className="bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300/60 transition-colors">
              <div className="w-full h-56 md:h-72 overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  title={`${featured.title} | Dental Safety Abroad`}
                  width={800}
                  height={400}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  loading="eager"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800 whitespace-nowrap">
                    {featured.category}
                  </span>
                  <span className="text-xs text-foreground-500">{featured.readTime}</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                  {featured.excerpt}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {featured.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                  Read article
                  <span className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </span>
              </div>
            </article>
          </Link>

          {/* Post Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="block group"
              >
                <article className="bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300/60 transition-colors h-full flex flex-col">
                  <div className="w-full h-44 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      title={`${post.title} | Dental Safety Abroad`}
                      width={800}
                      height={400}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 whitespace-nowrap">
                        {post.category}
                      </span>
                      <span className="text-xs text-foreground-500">{post.readTime}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {post.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors mt-auto">
                      Read article
                      <span className="w-4 h-4 flex items-center justify-center ml-1">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}